import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheck, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { apiGetRequest, apiCompleteRequest } from "../../../utils/queries";
import { Validate } from "../../../utils/Validator";
import { trackPromise } from "react-promise-tracker";
import Alerts from "../../molecules/Alerts";
import Confirm from "../../molecules/Confirm";
import { PolicyModal } from "./PolicyModal";
import { v4 as UUID } from "uuid";
import { Container, Row, Col, Card, CardBody, CardHeader, Button } from "reactstrap";
import { useModalCleanup } from "../../../utils/useModalCleanup";
import "./ReviewPage.css";

const ReviewPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const modalCleanup = useModalCleanup();

  const [account, setAccount] = useState({});
  const [bankDataList, setBankDataList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [formConfirmed, setFormConfirmed] = useState(false);
  const [formSOCConfirmed, setFormSOCConfirmed] = useState(false);
  const [formTOCConfirmed, setFormTOCConfirmed] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);


  /**
   * Load request data when token changes or window gains focus (in case user switched tabs and updated data elsewhere)
   * Also sets up and cleans up event listener for window focus because 
   */
  useEffect(() => {
    const getRequest = async () => {
      try {
        const response = await apiGetRequest(token);

        setAccount(response.data.request);
        setBankDataList(response.data.bankDataList || []);
        setFileList(response.data.files || response.data.fileList || []);
        setCountryList(response.data.countryList || []);
        setAccountLoaded(true);
      } catch (error) {
        addAlert("Error loading account data. Please refresh the page. " + error, "error");
      }
    };
    if (token) {
      trackPromise(getRequest());
    }

    const handleFocus = () => {
      if (token) {
        trackPromise(getRequest());
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [token]);


  const addAlert = (message, color, timeout = 5000) => {
    let id = UUID();
    setAlerts((prev) => [
      ...prev,
      { message: message, id: id, color: color, timeout: timeout },
    ]);

    // window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Used to show details about each bank data - same as FilesPage
   */
  const showBankDataDetails = (bankDataId) => {
    const bankData = bankDataList.find((bd) => bd.id === bankDataId);
    if (!bankData) return `Bank Account #${bankDataId}`;

    if (bankData.type === "REGNO/ACCOUNT") {
      return `${bankData.type}${bankData.bankAccountNumber ? ` - ${bankData.bankAccountNumber}` : ""}`;
    }

    if (bankData.type === "FIK") {
      return `${bankData.type}${bankData.fikNumber ? ` - ${bankData.fikNumber}` : ""}`;
    }

    return `${bankData.type && `${bankData.type} - `}${bankData.bank || `Bank Account #${bankData.id}`}
    ${bankData.currency?.name && ` (${bankData.currency.name})`}`;
  };

  const getFilesForBankData = (bankDataId) => {
    return fileList?.filter((file) => file?.bankDataId === bankDataId) || [];
  };


  /**
   * Used to complete the form by checking required confirmations and calling the complete API
   */
  const handleSubmitClick = () => {
    const requiredData = Validate.requiredData(bankDataList, fileList);
    if (!requiredData.isValid) {
      addAlert(requiredData.message, "error", 8000);
      return;
    }

    const requiredConfirmations = Validate.requiredConfirmations(formConfirmed, formSOCConfirmed, formTOCConfirmed);
    if (!requiredConfirmations.isValid) {
      addAlert(requiredConfirmations.message, "error", 8000);
      return;
    }

    setConfirmSubmitOpen(true);
  };

  const completeForm = async () => {
    let hasValidationErrors = false;

    // Use Validator to check required data for submission
    const requiredData = Validate.requiredData(bankDataList, fileList);
    if (!requiredData.isValid) {
      addAlert(requiredData.message, "error", 8000);
      hasValidationErrors = true;
    }

    const requiredConfirmations = Validate.requiredConfirmations(formConfirmed, formSOCConfirmed, formTOCConfirmed);
    if (!requiredConfirmations.isValid) {
      addAlert(requiredConfirmations.message, "error", 8000);
      hasValidationErrors = true;
    }

    if (hasValidationErrors) {
      return;
    }

    try {
      await trackPromise(apiCompleteRequest(token));
      navigate("/complete");
    } catch (error) {
      const serverMessage = error?.response?.data?.errors?.PaymentTerm?.[0];
      addAlert(serverMessage || "Error submitting form. Please try again. " + error, "error");
    }
  };


  const handleBack = () => {
    navigate(`/${token}/files`);
  };

  const openPolicyModal = (e) => {
    e.preventDefault();
    setPolicyModalOpen(true);
  };

  const rField = (label, value) => (
    <div className="vbdm__field">
      <div className="vbdm__field-label">{label}</div>
      <div className={`vbdm__field-value${!value ? ' vbdm__field-value--empty' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  );


  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <Alerts alerts={alerts} setAlerts={setAlerts} />
          <div className="vfp-header">
            <div className="vfp-header__step">Step 4 of 4</div>
            <h4 className="vfp-header__title">Review &amp; Submit</h4>
            <p className="vfp-header__sub">Review your details below and confirm acceptance of the required agreements to complete your submission.</p>
          </div>
          <div className="vfp-body">

            {accountLoaded && (
              <>
                {/* Account Summary */}
                <div className="rv-card mb-3">
                  <div className="vbdm__header">
                    <div className="vbdm__header-left">
                      <div className="vbdm__title">Account Information</div>
                    </div>
                  </div>
                  <div className="vbdm__body">
                    {account?.name || account?.address1 || account?.city ? (
                      <div className="vbdm__section">
                        <div className="vbdm__grid">
                          {rField("Account Name", account.name)}
                          {rField("Address Line 1", account.address1)}
                          {rField("Address Line 2", account.address2)}
                          {rField("City/Town", account.city)}
                          {rField("Postal Code", account.zip)}
                          {rField("Country", countryList.find(c => c.id === account.countryId)?.countryName)}
                          {rField("Phone", account.phone)}
                          {rField("Finance Email", account.financeEmail)}
                          {rField("Sales Email", account.salesEmail)}
                          {rField("Sales Contact Name", account.salesContactName)}
                          {rField("Sales Contact Phone", account.salesContactMobile)}
                          {rField("Payment Terms", account.paymentTermName)}
                          {rField("Tax Registration", account.taxRegistrationNumber)}
                          {rField("Notes", account.notes)}
                        </div>
                      </div>
                    ) : (
                      <div className="vbdm__section">
                        <div className="alert alert-warning mb-0">
                          Account information not completed. Please go back to Step 1 to fill in company details.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Details Summary */}
                <div className="rv-card mb-3">
                  <div className="vbdm__header">
                    <div className="vbdm__header-left">
                      <div className="vbdm__title">Bank Details</div>
                    </div>
                  </div>
                  <div className="vbdm__body">
                    {bankDataList.length > 0 ? (
                      bankDataList.map((bank, index) => (
                        <div key={index}>
                          {index > 0 && <div className="vbdm__divider" />}
                          <div className="vbdm__section">

                            {bank.type === "FIK" && (
                              <div className="vbdm__grid">
                                {rField("Kortart", bank.fikKortart)}
                                {rField("Kreditor/GIRO", bank.fikNumber ? bank.fikNumber.toString().replace(/^\+0?/, '') : null)}
                              </div>
                            )}

                            {bank.type === "REGNO/ACCOUNT" && (
                              <div className="vbdm__grid">
                                {rField("Registration Number", bank.regNo)}
                                {rField("Account Number", bank.bankAccountNumber)}
                                {rField("Currency", bank.currency?.name)}
                              </div>
                            )}

                            {bank.type === "ABA ROUTING NUMBER" && (
                              <>
                                <div className="vbdm__grid">
                                  {rField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                                  {rField("Account Number", bank.bankAccountNumber)}
                                  {rField("Currency", bank.currency?.name)}
                                </div>
                                <div className="vbdm__divider" />
                                <div className="vbdm__grid">
                                  {rField("Beneficiary Bank ABA Routing Number", bank.abaRoutingNumber)}
                                  {rField("Beneficiary Bank Name", bank.bank)}
                                  {rField("Beneficiary Bank Address", bank.address)}
                                  {rField("Beneficiary Bank City", bank.city)}
                                  {rField("Beneficiary Bank Postal Code", bank.zip)}
                                  {rField("Beneficiary Bank Country", bank.country)}
                                </div>
                                {bank.correspondentBankABARoutingNumber && (
                                  <>
                                    <div className="vbdm__divider" />
                                    <div className="vbdm__grid">
                                      {rField("Correspondent Bank ABA Routing Number", bank.correspondentBankABARoutingNumber)}
                                      {rField("Correspondent Bank Name", bank.correspondentBankName)}
                                      {rField("Correspondent Bank Address", bank.correspondentBankAddress)}
                                      {rField("Correspondent Bank City", bank.correspondentBankCity)}
                                      {rField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                                      {rField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                                    </div>
                                  </>
                                )}
                              </>
                            )}

                            {bank.type === "BIC" && (
                              <>
                                <div className="vbdm__grid">
                                  {rField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                                  {rField("Account Number", bank.bankAccountNumber)}
                                  {rField("Currency", bank.currency?.name)}
                                </div>
                                <div className="vbdm__divider" />
                                <div className="vbdm__grid">
                                  {rField("Beneficiary Bank SWIFT", bank.bic)}
                                  {rField("Beneficiary Bank Name", bank.bank)}
                                  {rField("Beneficiary Bank Address", bank.address)}
                                  {rField("Beneficiary Bank City", bank.city)}
                                  {rField("Beneficiary Bank Postal Code", bank.zip)}
                                  {rField("Beneficiary Bank Country Name", bank.country)}
                                  {rField("Beneficiary Bank Country Code", bank.countryCode)}
                                </div>
                                {bank.correspondentBankSwift && (
                                  <>
                                    <div className="vbdm__divider" />
                                    <div className="vbdm__grid">
                                      {rField("Correspondent Bank SWIFT", bank.correspondentBankSwift)}
                                      {rField("Correspondent Bank Name", bank.correspondentBankName)}
                                      {rField("Correspondent Bank Address", bank.correspondentBankAddress)}
                                      {rField("Correspondent Bank City", bank.correspondentBankCity)}
                                      {rField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                                      {rField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                                      {rField("Correspondent Bank Country Code", bank.correspondentBankCountryCode)}
                                    </div>
                                  </>
                                )}
                              </>
                            )}

                            {bank.type === "IBAN" && (
                              <>
                                <div className="vbdm__grid">
                                  {rField("Beneficiary Name", bank.beneficiaryBankAccountName)}
                                  {rField("IBAN", bank.iban)}
                                  {rField("Currency", bank.currency?.name)}
                                  {rField("Beneficiary Bank Name", bank.bank)}
                                  {rField("Beneficiary Bank Address", bank.address)}
                                  {rField("Beneficiary Bank City", bank.city)}
                                  {rField("Beneficiary Bank Postal Code", bank.zip)}
                                  {rField("Beneficiary Bank Country Name", bank.country)}
                                  {rField("Beneficiary Bank Country Code", bank.countryCode)}
                                </div>
                                {bank.correspondentBankSwift && (
                                  <>
                                    <div className="vbdm__divider" />
                                    <div className="vbdm__grid">
                                      {rField("Correspondent Bank SWIFT", bank.correspondentBankSwift)}
                                      {rField("Correspondent Bank Name", bank.correspondentBankName)}
                                      {rField("Correspondent Bank Address", bank.correspondentBankAddress)}
                                      {rField("Correspondent Bank City", bank.correspondentBankCity)}
                                      {rField("Correspondent Bank Postal Code", bank.correspondentBankPostalCode)}
                                      {rField("Correspondent Bank Country Name", bank.correspondentBankCountryName)}
                                      {rField("Correspondent Bank Country Code", bank.correspondentBankCountryCode)}
                                    </div>
                                  </>
                                )}
                              </>
                            )}


                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="vbdm__section">
                        <div className="alert alert-warning mb-0">
                          No bank details added. Please go back to Step 2 to add at least one bank detail before submitting.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Files Summary */}
                <div className="rv-card mb-3">
                  <div className="vbdm__header">
                    <div className="vbdm__header-left">
                      <div className="vbdm__title">Files</div>
                    </div>
                  </div>
                  <div className="vbdm__body">
                    {fileList.length > 0 ? (
                      <div className="vbdm__section">
                        <div className="rd-file-list">
                          {fileList.map((file, index) => {
                            const bank = bankDataList.find(b => b.id === file.bankDataId);
                            const bankLabel = bank ? (bank.bank || showBankDataDetails(bank.id)) : 'Other';
                            return (
                              <div key={index} className="rd-file-item">
                                <div className="rd-file-item__icon">
                                  <span>📄</span>
                                </div>
                                <div className="rd-file-item__info">
                                  <a href={file?.url} target="_blank" rel="noopener noreferrer" className="rd-file-item__name">
                                    {file?.displayName || file?.fileName || file?.name}
                                  </a>
                                  <span className="rd-file-item__meta">{bankLabel}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="vbdm__section">
                        <div className="alert alert-warning mb-0">
                          No files uploaded. Please go back to Step 3 to upload at least one supporting document before submitting.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Confirmation checkboxes */}
                <div className="rv-card mb-3">
                  <div className="vbdm__header">
                    <div className="vbdm__header-left">
                      <div className="vbdm__title">Required Confirmations</div>
                    </div>
                  </div>
                  <div className="vbdm__body">
                    <div className="vbdm__section">
                      <div className="rv-check-list">
                        <label className="rv-check-item">
                          <input type="checkbox" onChange={() => setFormConfirmed(!formConfirmed)} />
                          <span>I have read and understand the <a href="#0" onClick={openPolicyModal}>PERSONAL DATA POLICY</a></span>
                        </label>
                        <label className="rv-check-item">
                          <input type="checkbox" onChange={() => setFormSOCConfirmed(!formSOCConfirmed)} />
                          <span>I acknowledge and accept Hafnia's <a href="https://hafniabw.com/wp-content/uploads/sites/5/2023/11/Supplier-Code-of-Conduct.pdf" target="_blank" rel="noopener noreferrer">SUPPLIER CODE OF CONDUCT.</a></span>
                        </label>
                        <label className="rv-check-item">
                          <input type="checkbox" onChange={() => setFormTOCConfirmed(!formTOCConfirmed)} />
                          <span>I acknowledge and accept Hafnia's <a href="https://bw-content.shippalm.net/sp3bwfiles/TandC/Hafnia%20Short%20Form%20Terms%20and%20Conditions.pdf" target="_blank" rel="noopener noreferrer">GENERAL TERMS AND CONDITIONS FOR PURCHASE OF GOODS &amp; SERVICES.</a></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mt-4 d-flex justify-content-between">
              <Button className="btn-back" onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back: Upload Files
              </Button>
              <Button
                className="btn-submit"
                size="lg"
                onClick={handleSubmitClick}
              >
                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                Submit Form
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Policy Modal */}
      <PolicyModal toggle={policyModalOpen} setToggle={setPolicyModalOpen} />

      <Confirm
        toggle={confirmSubmitOpen}
        setToggle={setConfirmSubmitOpen}
        title="Confirm"
        text="Submit this form to Hafnia? Once submitted you will not be able to make further changes."
        onConfirm={completeForm}
      />
    </Container>
  );
};

export default ReviewPage;
